import { api } from './api';

// Customer-facing billing client for the InnoPayPG / Omniware one-time checkout.
//
// Flow: getPlans() tells the UI whether payments are enabled and what the paid
// plans cost (no auth, no secrets). checkout(plan) asks the server to price the
// plan, create an order, and return the Omniware auto-submit form; submitCheckout
// renders that form and POSTs it, navigating the browser to the hosted payment
// page. Omniware later redirects back to /?payment=success|failed, which the app
// reads on load (see readPaymentResult).

export interface BillingPlanOption {
  plan: string;
  amount: number;
}

export interface BillingPlans {
  enabled: boolean;
  currency: string;
  duration_days: number;
  plans: BillingPlanOption[];
}

export interface CheckoutForm {
  order_id: string;
  action_url: string;
  method: string; // "POST"
  fields: Record<string, string>;
}

export const billingService = {
  // Public — safe to call for anonymous users; returns enabled=false when payments
  // are off or the gateway isn't configured.
  async getPlans(): Promise<BillingPlans> {
    const res = await api.get<unknown>('/billing/plans');
    const b = (res.data ?? {}) as Record<string, unknown>;
    const rawPlans = Array.isArray(b.plans) ? (b.plans as unknown[]) : [];
    return {
      enabled: b.enabled === true,
      currency: typeof b.currency === 'string' ? b.currency : 'INR',
      duration_days: typeof b.duration_days === 'number' ? b.duration_days : 30,
      plans: rawPlans.map((p) => {
        const r = (p ?? {}) as Record<string, unknown>;
        return { plan: String(r.plan ?? ''), amount: typeof r.amount === 'number' ? r.amount : Number(r.amount) || 0 };
      }),
    };
  },

  // Authed — creates the order and returns the Omniware form to submit.
  async checkout(plan: string, contact?: { name?: string; phone?: string }): Promise<CheckoutForm> {
    const res = await api.post<unknown>('/billing/checkout', {
      plan,
      name: contact?.name,
      phone: contact?.phone,
    });
    const b = (res.data ?? {}) as Record<string, unknown>;
    return {
      order_id: String(b.order_id ?? ''),
      action_url: String(b.action_url ?? ''),
      method: String(b.method ?? 'POST'),
      fields: (b.fields ?? {}) as Record<string, string>,
    };
  },
};

// Build a hidden form from the checkout response and submit it, navigating the
// browser to the gateway's hosted payment page. This is a full page navigation —
// the customer returns via the /?payment= redirect.
export function submitCheckout(form: CheckoutForm): void {
  if (!form.action_url || !form.fields) return;
  const el = document.createElement('form');
  el.method = (form.method || 'POST').toUpperCase();
  el.action = form.action_url;
  el.style.display = 'none';
  for (const [k, v] of Object.entries(form.fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = String(v);
    el.appendChild(input);
  }
  document.body.appendChild(el);
  el.submit();
}

// Read the ?payment=success|failed|error the gateway callback redirects back to,
// then strip it from the URL so a refresh doesn't re-trigger the toast.
export function readPaymentResult(): 'success' | 'failed' | 'error' | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const val = params.get('payment');
  if (val !== 'success' && val !== 'failed' && val !== 'error') return null;
  params.delete('payment');
  const qs = params.toString();
  const clean = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
  window.history.replaceState({}, '', clean);
  return val;
}
